import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, Checkbox, Chip, LinearProgress, makeStyles, Typography } from "@material-ui/core";
import * as _ from 'lodash';
import PropTypes from "prop-types";

import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TablePagination from "@material-ui/core/TablePagination";
import { Info } from "@material-ui/icons";

import AppTable from "../Table/AppTable";
import TablePaginationActions from "../Table/TablePaginationActions";
// import AppUtil from "utilities/AppUtil";
import InputSearch from "components/Input/InputSearch";
import {
  fetchListParticipant,
  getParticipantList
} from "store/slices/coordinatorParticipant";
import AppConfirmDialog from "components/Dialog/AppConfirmDialog";
import AppConfig from "config/AppConfig";
import AppColor from "utilities/AppColor";
import AppDialog from "components/Dialog/AppDialog";
import ParticipantForm from "views/Client/Participant/Components/ParticipantForm";

const { APPROVAL_STATUS } = AppConfig;

const headCells = [
  {
    id: "name",
    numeric: "left",
    disablePadding: false,
    label: "label.name",
    sort: true,
  },
  {
    id: "email",
    numeric: "left",
    disablePadding: false,
    label: "label.email",
    sort: true,
  },
  {
    id: "phone",
    numeric: "left",
    disablePadding: false,
    label: "label.phone",
    sort: true,
  },
  {
    id: "status",
    numeric: "left",
    disablePadding: false,
    label: "label.status",
    sort: true,
  },
  {
    id: "actions",
    numeric: "center",
    disablePadding: false,
    label: "label.actions",
    sort: false,
  },
];

const styles = makeStyles(() => ({
  title: {
    display: 'flex',
    margin: '1rem 0 1rem 0',
    '& h5': {
      color: AppColor.orange,
    }
  },
}));

const CoordinatorParticipantTable = (props) => {
  const {
    clientId
  } = props
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const classes = styles();

  const [queries, setQueries] = useState({
    sort: true,
    page_number: 1,
    page_size: 10,
    search_key: "",
    sort_field: "clientName",
  });
  const clientList = useSelector(getParticipantList);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [selected, setSelected] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showParticipantDetail, setShowParticipantDetail] = useState(false);
  const [participantId, setParticipantId] = useState();

  const [searchKey, setSearchKey] = useState("");

  const emptyRows = data.length === 0;

  const _handleDeleteClients = () => {

    setShowDeleteModal(false);
  }

  const _handleSelect = (id) => {
    if (!selected.includes(id)) {
      setSelected([...selected, id]);
    } else {
      setSelected(selected.filter(item => item !== id))
    }
  }

  const _handleSelectAll = () => {
    if (data.length === selected.length) {
      setSelected([]);
    } else {
      setSelected(_.union(selected, data.map(item => item.id)));
    }
  }

  const handleChangePage = (event, newPage) => {
    _loadData({ ...queries, page_number: newPage + 1 });
    // AppUtil.scrollToTopById("main-panel");
  };

  const handleChangeRowsPerPage = (event) => {
    _loadData({
      ...queries,
      page_number: 1,
      page_size: parseInt(event.target.value),
    });
    // AppUtil.scrollToTopById("main-panel");
  };

  const onRequestSort = (event, property) => {
    _loadData({
      ...queries,
      sort_field: property,
      sort:
        property === queries.sort_field ? queries.sort !== true : true,
    });
  };

  const onSearch = (value) => {
    setSearchKey(value);
    _loadData({
      ...queries,
      search_key: value,
      page_number: 1,
    });
  };

  const _loadData = async ({ page_number, page_size, search_key, sort, sort_field } = {}) => {
    const newQueries = {
      ...queries,
    };
    if (page_number) {
      newQueries.page_number = page_number;
    }
    if (page_size) {
      newQueries.page_size = page_size;
    }
    if (sort_field) {
      newQueries.sort_field = sort_field;
    }
    newQueries.search_key = search_key || "";
    newQueries.sort = !(!sort || false);
    setLoading(true);
    dispatch(fetchListParticipant({ ...newQueries, clientId }))
      .then(({ payload }) => {
        setQueries(newQueries);
        setLoading(false);
        if (payload) {
          setTotalElements(payload.totalElements);
        }
      });
  };

  useEffect(() => {
    _loadData({
      page_number: 1,
      page_size: 10,
      sort_field: "createdDate",
      sort: false,
    });
  }, []);

  useEffect(() => {
    if (clientList)
      setData(clientList);
  }, [clientList]);

  const onSelectParticipant = (id) => {
    setParticipantId(id);
    setShowParticipantDetail(true);
  };

  const onCloseParticipantDetail = () => {
    _loadData();
    setParticipantId(null);
    setShowParticipantDetail(false);
  };

  return (
    <div>
      <Box mb={3}>
        <strong>
          {t("title.participantList")}
        </strong>
      </Box>
      <Box my={3}>
        <InputSearch
          onSearch={onSearch}
          searchText={searchKey}
        />
      </Box>
      <AppTable
        onRequestSort={onRequestSort}
        order={queries.sort === true ? "asc" : "desc"}
        orderBy={queries.sort_field}
        rowCount={data.length}
        headCells={headCells}
        numSelected={selected.length}
        onSelectAllClick={_handleSelectAll}
      >
        <TableBody>
          {loading && (<TableRow>
            <TableCell colSpan={7} padding="none">
              <LinearProgress />
            </TableCell>
          </TableRow>)}
          {data.map((row, index) => (
            <TableRow key={index}>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selected.indexOf(row.id) !== -1}
                  inputProps={{ 'aria-labelledby': row.id }}
                  onClick={() => _handleSelect(row.id)}
                  color="primary"
                />
              </TableCell>
              <TableCell>{`${row.name}`}</TableCell>
              <TableCell>{row.email}</TableCell>
              <TableCell>{row.phone}</TableCell>
              <TableCell>
                <Chip
                  style={row.approvalStatus !== APPROVAL_STATUS.PENDING ? {
                    backgroundColor: APPROVAL_STATUS.WAITING_FOR_APPROVAL === row.approvalStatus
                      ? AppColor.orange
                      : APPROVAL_STATUS.ACTIVE === row.approvalStatus
                        ? AppColor.green
                        : AppColor.red,
                    color: AppColor.white
                  } : {}}
                  size="small"
                  label={t(`approval_status.${row.approvalStatus}`)}
                />
              </TableCell>
              <TableCell align="center">
                <Button
                  onClick={() => onSelectParticipant(row.id)}
                  variant="text"
                >
                  <Info />
                </Button>
              </TableCell>
            </TableRow>
          ))}

          {emptyRows > 0 && (
            <TableRow style={{ height: 53 }}>
              <TableCell colSpan={headCells.length} className="text-center">
                {t("label.no_data")}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              colSpan={headCells.length}
              count={totalElements}
              rowsPerPage={queries.page_size}
              page={queries.page_number - 1}
              SelectProps={{
                inputProps: { "aria-label": "rows per page" },
                native: true,
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
              labelRowsPerPage={`${t("label.rows_per_page")}:`}
              labelDisplayedRows={({ from, to, count }) =>
                t("label.from_to", {
                  from,
                  to,
                  count:
                    count !== -1 ? count : `${t("text.more_than")} ${to}`,
                })
              }
            />
          </TableRow>
        </TableFooter>
      </AppTable>
      <AppDialog open={showParticipantDetail} noCloseButton noTitle>
        <Box my={2}>
          <Box className={classes.title}>
            <Typography variant="h5">
              {t("label.editParticipant")}
            </Typography>
          </Box>
          <ParticipantForm
            participantId={participantId}
            onCancel={() => onCloseParticipantDetail()}
            isAdmin={true}
          />
        </Box>
      </AppDialog>
      <AppConfirmDialog
        open={showDeleteModal}
        onConfirm={() => _handleDeleteClients()}
        onClose={() => setShowDeleteModal(false)}
        title={t('title.delete_clients')}
        content={t('text.delete_clients')}
      />
    </div>
  );
};

CoordinatorParticipantTable.propTypes = {
  clientId: PropTypes.any
};

export default CoordinatorParticipantTable;
